import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import apiClient from '@/lib/api'
import { getCookie, setCookie, deleteCookie } from 'cookies-next'
import type { LoginSecurityResponse } from '@/lib/api/services/auth.service'

// 90 dias em segundos para expiração dos cookies de autenticação
const COOKIE_MAX_AGE = 90 * 24 * 60 * 60 // 7776000 segundos

interface User {
  id: string
  email: string
  roles: string[]
  twoFactorEnabled: boolean
  name?: string
  avatar?: string
}

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isNavigating: boolean

  // Actions
  login: (email: string, password: string, mentorId?: string) => Promise<void>
  /** Applies a login-shaped response (e.g. the one-time access exchange) to the store and cookies. */
  applySession: (data: LoginSecurityResponse) => void
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  updateUser: (user: User) => void
  clearAuth: () => void
  setNavigating: (isNavigating: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      isNavigating: false,

      login: async (email: string, password: string, mentorId?: string) => {
        set({ isLoading: true })
        try {
          const data = await apiClient.login(email, password, mentorId)
          get().applySession(data)
          set({ isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      applySession: (data: LoginSecurityResponse) => {
        // O login devolve first_name/last_name, não `name`: monta o nome aqui
        // para o menu não cair no "Usuário" até o próximo checkAuth.
        const loginUser = data.user
        const name =
          loginUser?.name ||
          [loginUser?.first_name, loginUser?.last_name].filter(Boolean).join(' ').trim() ||
          undefined
        const user = loginUser ? ({ ...loginUser, name } as User) : null
        set({
          user,
          isAuthenticated: true
        })
        if (user) {
          setCookie('user', JSON.stringify(user), { maxAge: COOKIE_MAX_AGE })
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          await apiClient.logout()
        } catch (error) {
          console.warn('Logout API failed, proceeding with local sign-out:', error)
        } finally {
          deleteCookie('visitor_mode')
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false
          })
        }
      },

      checkAuth: async () => {
        set({ isLoading: true })
        try {
          const token = getCookie('access_token')
          if (!token) {
            set({
              user: null,
              isAuthenticated: false
            })
            return
          }

          // Tentar obter user do store primeiro
          let currentUser = get().user
          let userIdOrEmail = currentUser?.id || currentUser?.email

          // Se não tem no store, tentar recuperar do cookie
          if (!userIdOrEmail) {
            const userCookie = getCookie('user')
            if (userCookie) {
              try {
                const parsedUser = typeof userCookie === 'string'
                  ? JSON.parse(userCookie)
                  : userCookie
                userIdOrEmail = parsedUser?.id || parsedUser?.email
                // Restaurar user no store a partir do cookie
                if (parsedUser && (parsedUser.id || parsedUser.email)) {
                  currentUser = parsedUser as User
                  set({ user: currentUser, isAuthenticated: true })
                }
              } catch {
                // Cookie inválido, ignorar
              }
            }
          }

          // Se ainda não tem identificador, apenas marcar como não autenticado
          // (não limpar cookies agressivamente - pode ser um problema de timing)
          if (!userIdOrEmail) {
            set({ isAuthenticated: false })
            return
          }

          // Verificar se o token ainda é válido buscando o profile
          const response = await apiClient.getProfile(userIdOrEmail)

          // Mapear resposta da API para o tipo User do store
          const updatedUser: User = {
            id: response.id,
            email: response.email,
            roles: currentUser?.roles || [], // Manter roles do user atual
            twoFactorEnabled: currentUser?.twoFactorEnabled || false,
            name: response.name || currentUser?.name,
            avatar: response.avatar || undefined,
          }

          set({
            user: updatedUser,
            isAuthenticated: true
          })
          // Regrava o cookie `user` com o `name` (o login só grava id/email/roles).
          // O interceptor do http-client lê este cookie para emitir os headers
          // x-user-id/x-user-email/x-user-name; sem isto, x-user-name nunca é enviado.
          setCookie('user', JSON.stringify(updatedUser), { maxAge: COOKIE_MAX_AGE })
        } catch (error) {
          const axiosError = error as { response?: { status?: number } }
          const token = getCookie('access_token')
          const currentUser = get().user

          if (axiosError?.response?.status === 401) {
            get().clearAuth()
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            })
            return
          }

          // Em caso de erro de rede ou API, não deslogar imediatamente
          // Apenas logar o erro e manter o estado atual se houver token
          console.error('Error verifying auth token:', error)

          // Se tem token e user no store, manter autenticado (erro pode ser temporário)
          if (token && currentUser?.id) {
            set({ isAuthenticated: true })
          } else {
            // Só limpar se realmente não tem credenciais
            set({ isAuthenticated: false })
          }
        } finally {
          // Sempre resetar loading, independente do resultado
          set({ isLoading: false })
        }
      },

      updateUser: (user: User) => {
        set({ user })
        setCookie('user', JSON.stringify(user), { maxAge: COOKIE_MAX_AGE })
      },

      clearAuth: () => {
        deleteCookie('access_token')
        deleteCookie('refresh_token')
        deleteCookie('user')
        deleteCookie('visitor_mode')
        set({
          user: null,
          isAuthenticated: false,
          isNavigating: false
        })
      },

      setNavigating: (isNavigating: boolean) => {
        set({ isNavigating })
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
    }
  )
)